/**
 * Frame Parser for Protocol B
 *
 * Parses raw frames into structured data with detailed field explanations.
 * Designed to be educational - each field includes documentation about its purpose.
 */

import { ControlCodes, Channel, NodeLevel, FrameType } from '../types/protocol';
import { decodeData } from './encoding';
import { verifyCRC, combineCRC } from './crc16';

// =============================================================================
// PARSED FRAME TYPES
// =============================================================================

export interface ParsedBit {
  position: number;      // Bit position (0-7)
  value: boolean;        // Bit value
  name: string;          // Field name
  description: string;   // What this bit means
}

export interface ParsedByte {
  offset: number;        // Byte offset in frame
  value: number;         // Raw byte value
  hex: string;           // Hex representation
  binary: string;        // Binary representation
  name: string;          // Field name
  description: string;   // What this byte represents
  bits?: ParsedBit[];    // Bit-level breakdown (if applicable)
}

export interface ParsedPhysicalId {
  raw: number;           // Raw 16-bit value
  host: number;          // Host ID (0-2)
  central: number;       // Central ID (1-62, 0=N/A)
  regulator: number;     // Regulator ID (1-62, 0=N/A)
  subregulator: number;  // Subregulator ID (1-2, 0=N/A)
  level: NodeLevel;      // Detected node level
  formatted: string;     // Human-readable format "H0-C1-R5-S0"
  explanation: string;   // Detailed explanation
}

export interface ParsedHeader {
  bytes: ParsedByte[];
  destinationChannel: number;
  sourceChannel: number;
  isResponse: boolean;
  isRouted: boolean;
  isMaintenance: boolean;
  expectsResponse: boolean;
  isContinuation: boolean;
  hasError: boolean;
  isCommunicationError: boolean;
  destination: ParsedPhysicalId;
  source: ParsedPhysicalId;
  counter: number;
}

export interface ParsedCommand {
  code: number;
  name: string;
  category: string;
  description: string;
  parameters: ParsedParameter[];
  expectedResponse: string;
}

export interface ParsedParameter {
  name: string;
  offset: number;
  length: number;
  rawValue: Uint8Array;
  decodedValue: string | number;
  description: string;
  unit?: string;
}

export interface ParsedFrame {
  frameType: FrameType;
  frameTypeName: string;
  frameTypeDescription: string;
  raw: Uint8Array;
  rawHex: string;

  // For test frames (1 byte)
  testCode?: number;
  testName?: string;
  testDescription?: string;

  // For flow control frames
  flowControlType?: string;
  flowControlDescription?: string;

  // For data frames
  stx?: ParsedByte;
  encodedData?: Uint8Array;
  decodedData?: Uint8Array;
  header?: ParsedHeader;
  command?: ParsedCommand;
  payload?: ParsedByte[];
  crc?: {
    received: number;
    calculated: number;
    valid: boolean;
    bytes: ParsedByte[];
  };
  eof?: ParsedByte;

  // Educational summary
  summary: string;
  educationalNotes: string[];
}

// =============================================================================
// COMMAND DICTIONARY
// =============================================================================

interface CommandDefinition {
  code: number;
  name: string;
  category: string;
  description: string;
  parameters: ParameterDefinition[];
  responseDescription: string;
}

interface ParameterDefinition {
  name: string;
  length: number;  // -1 = variable
  description: string;
  unit?: string;
  decoder?: (data: Uint8Array) => string | number;
}

export const COMMAND_DICTIONARY: Record<number, CommandDefinition> = {
  // Time management
  0xBF46: {
    code: 0xBF46,
    name: 'CONSULTA_HORA',
    category: 'Mantenimiento de hora',
    description: 'Consulta la hora actual del regulador. El regulador responde con la fecha (días julianos desde 1-1-1980) y hora (décimas de segundo desde medianoche).',
    parameters: [],
    responseDescription: '2 bytes fecha + 4 bytes hora en décimas'
  },
  0xC620: {
    code: 0xC620,
    name: 'PUESTA_EN_HORA',
    category: 'Mantenimiento de hora',
    description: 'Establece la fecha y hora del regulador. Importante para sincronización de planes coordinados y registro de alarmas.',
    parameters: [
      { name: 'fecha', length: 2, description: 'Días julianos desde 1-1-1980', decoder: (d) => `Día ${(d[0] << 8) | d[1]}` },
      { name: 'hora', length: 4, description: 'Décimas de segundo desde medianoche', unit: 'décimas', decoder: (d) => {
        const tenths = (d[0] << 24) | (d[1] << 16) | (d[2] << 8) | d[3];
        const secs = Math.floor(tenths / 10);
        const h = Math.floor(secs / 3600);
        const m = Math.floor((secs % 3600) / 60);
        const s = secs % 60;
        return `${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}:${s.toString().padStart(2,'0')}.${tenths % 10}`;
      }}
    ],
    responseDescription: 'Mensaje sin datos (confirmación)'
  },

  // Detector management
  0x81: {
    code: 0x81,
    name: 'TABLAS_DETECTORES',
    category: 'Gestión de detectores',
    description: 'Asigna detectores a un grupo CCI (Ciclo Coordinado Idéntico) para medidas de intensidad y ocupación. Un CCI agrupa detectores con el mismo período de integración.',
    parameters: [
      { name: 'acción', length: 1, description: '0=Asignar, 1=Borrar grupo, 2=Borrar todos' },
      { name: 'grupo_CCI', length: 1, description: 'Número de grupo CCI (solo para acciones 0 y 1)' },
      { name: 'máscara_detectores', length: 4, description: 'Bits indican detectores a asignar (bit 0 = detector 1)' }
    ],
    responseDescription: 'Sin datos si OK, o código de error'
  },
  0x82: {
    code: 0x82,
    name: 'PETICION_DATOS_DETECTORES',
    category: 'Gestión de detectores',
    description: 'Solicita envío periódico de datos de detectores. El regulador enviará datos acumulados (intensidad, ocupación, cola) al final de cada intervalo.',
    parameters: [
      { name: 'grupo_CCI', length: 1, description: 'Grupo de detectores a consultar' },
      { name: 'hora_inicio', length: 3, description: 'Hora del primer envío (décimas de segundo)' },
      { name: 'intervalo', length: 1, description: 'Período de envío en segundos', unit: 's' }
    ],
    responseDescription: 'Sin datos si OK, o error si grupo no existe'
  },

  // Plan management
  0x83: {
    code: 0x83,
    name: 'PLAN_0',
    category: 'Gestión de planes',
    description: 'Envía el Plan 0 (plan externo/inscrito) desde el centro de control. Define estructura, tiempos de fases, ciclo y desfase para coordinación semafórica.',
    parameters: [
      { name: 'referencia', length: 3, description: 'Referencia en décimas desde lunes 00:00 (sincronismo semanal)' },
      { name: 'índices', length: 2, description: 'Bits 12-15: estructura, 8-11: transiciones, 4-7: tiempos mín, 0-3: atributos' },
      { name: 'desfase', length: 2, description: 'Desfase respecto al sincronismo', unit: 'décimas' },
      { name: 'ciclo', length: 2, description: 'Duración total del ciclo', unit: 'décimas' },
      { name: 'tiempos_fases', length: -1, description: '2 bytes por fase: duración de cada fase en décimas' }
    ],
    responseDescription: 'Sin datos si OK, o código de error con fase problemática'
  },
  0x84: {
    code: 0x84,
    name: 'SELECCION_PLAN',
    category: 'Gestión de planes',
    description: 'Selecciona el estado del regulador y/o el plan de tráfico. Permite cambiar entre modos: desconectado, apagado, intermitente, local, centralizado.',
    parameters: [
      { name: 'estado', length: 1, description: '0=Desconectado, 1=Apagado, 2=Intermitente, 3=Local, 4=Por demanda, 6=Centralizado, 8=Fin de fase' },
      { name: 'plan', length: 1, description: 'Número de plan (solo en estado 6 u 8)' },
      { name: 'referencia', length: 3, description: 'Referencia de sincronismo (-1 = usar última)' }
    ],
    responseDescription: 'Mensaje sin datos'
  },
  0x85: {
    code: 0x85,
    name: 'PLAN_EN_CURSO',
    category: 'Gestión de planes',
    description: 'Consulta los datos del plan que está ejecutando el regulador actualmente. Devuelve estructura, tiempos, ciclo y desfase.',
    parameters: [],
    responseDescription: 'Formato igual que Plan 0: referencia + índices + desfase + ciclo + tiempos'
  },

  // State notifications
  0xC4: {
    code: 0xC4,
    name: 'NOTIF_CAMBIOS_DETECTORES',
    category: 'Notificaciones',
    description: 'Activa el envío en tiempo real de cambios de estado de detectores físicos o lógicos. Cada cambio genera un mensaje espontáneo.',
    parameters: [
      { name: 'tipo', length: 1, description: '0=Físicos, 1=Lógicos' },
      { name: 'máscara', length: 4, description: 'Bits indican detectores a monitorizar (0=cancelar)' }
    ],
    responseDescription: 'Estado actual de detectores si OK, o código de error'
  },
  0xC5: {
    code: 0xC5,
    name: 'NOTIF_CAMBIOS_ESTADO',
    category: 'Notificaciones',
    description: 'Consulta estado actual y activa envío de cambios de estado del regulador. Cada cambio de modo genera un mensaje espontáneo.',
    parameters: [],
    responseDescription: 'Estado actual + origen del cambio + fecha/hora'
  },
  0xD0: {
    code: 0xD0,
    name: 'NOTIF_CAMBIOS_POSICION',
    category: 'Notificaciones',
    description: 'Consulta posición actual de los semáforos y activa envío de cambios. Útil para visualización en tiempo real.',
    parameters: [
      { name: 'timeout', length: 1, description: 'Tiempo de expiración en minutos (opcional, default=10)' }
    ],
    responseDescription: 'Código de color de cada grupo semafórico'
  },
  0xD6: {
    code: 0xD6,
    name: 'NOTIF_CAMBIOS_VARIOS',
    category: 'Notificaciones',
    description: 'Configuración flexible de notificaciones: estado, plan, fase y/o posición. Permite seleccionar qué eventos generan mensajes.',
    parameters: [
      { name: 'máscara', length: 1, description: 'Bit 0=estado, 1=plan, 2=fase, 3=posición (0=cancelar)' },
      { name: 'timeout', length: 1, description: 'Minutos de expiración (0xFE=sin timeout)' }
    ],
    responseDescription: 'Estado completo: estado+plan+fase+transición+posición+hora'
  },

  // Alarm management
  0xC841: {
    code: 0xC841,
    name: 'HABILITAR_ENVIOS',
    category: 'Gestión de alarmas',
    description: 'Habilita o deshabilita el envío de mensajes espontáneos por tipo. Controla qué eventos generan notificaciones automáticas.',
    parameters: [
      { name: 'máscara', length: 1, description: 'Bit 7=habilitar/deshabilitar, bits 0-6=tipos de mensaje' }
    ],
    responseDescription: 'Mensaje sin datos'
  },
  0xC850: {
    code: 0xC850,
    name: 'CONSULTA_HISTORICO',
    category: 'Gestión de alarmas',
    description: 'Devuelve alarmas activas e histórico desde un instante. Activa el envío de nuevas alarmas.',
    parameters: [
      { name: 'fecha', length: 2, description: 'Fecha desde la que consultar (días julianos)' },
      { name: 'hora', length: 3, description: 'Hora desde la que consultar (décimas)' }
    ],
    responseDescription: 'Lista de alarmas activas + histórico'
  },
  0xC842: {
    code: 0xC842,
    name: 'BORRAR_HISTORICO',
    category: 'Gestión de alarmas',
    description: 'Borra el histórico de alarmas hasta un instante. Opcionalmente habilita el envío de nuevas alarmas.',
    parameters: [
      { name: 'fecha', length: 2, description: 'Fecha hasta la que borrar' },
      { name: 'hora', length: 3, description: 'Hora hasta la que borrar' }
    ],
    responseDescription: 'Mensaje sin datos'
  },

  // Equipment test
  0xD445: {
    code: 0xD445,
    name: 'TEST_EQUIPOS',
    category: 'Diagnóstico',
    description: 'Test periódico para verificar que el equipo tiene habilitados los envíos de alarmas y cambios de estado.',
    parameters: [],
    responseDescription: 'Máscara de envíos habilitados actualmente'
  },

  // Direct control
  0x8A: {
    code: 0x8A,
    name: 'ESTADO_GRUPOS_MANDO',
    category: 'Mando directo',
    description: 'Consulta el estado de los grupos de mando directo (salidas no destinadas a tráfico regular).',
    parameters: [],
    responseDescription: '2 bytes por grupo: estado de salidas R/A/V + errores de color'
  }
};

// Single-byte commands (first byte identifies the command)
export const SINGLE_BYTE_COMMANDS: Record<number, Omit<CommandDefinition, 'code'>> = {
  0x81: COMMAND_DICTIONARY[0x81],
  0x82: COMMAND_DICTIONARY[0x82],
  0x83: COMMAND_DICTIONARY[0x83],
  0x84: COMMAND_DICTIONARY[0x84],
  0x85: COMMAND_DICTIONARY[0x85],
  0x8A: COMMAND_DICTIONARY[0x8A],
  0xC4: COMMAND_DICTIONARY[0xC4],
  0xC5: COMMAND_DICTIONARY[0xC5],
  0xD0: COMMAND_DICTIONARY[0xD0],
  0xD6: COMMAND_DICTIONARY[0xD6]
};

// Two-byte commands (first two bytes identify the command)
export const TWO_BYTE_COMMANDS: Record<number, Omit<CommandDefinition, 'code'>> = {
  0xBF46: COMMAND_DICTIONARY[0xBF46],
  0xC620: COMMAND_DICTIONARY[0xC620],
  0xC841: COMMAND_DICTIONARY[0xC841],
  0xC850: COMMAND_DICTIONARY[0xC850],
  0xC842: COMMAND_DICTIONARY[0xC842],
  0xD445: COMMAND_DICTIONARY[0xD445]
};

// =============================================================================
// CHANNEL DESCRIPTIONS
// =============================================================================

export const CHANNEL_INFO: Record<number, { name: string; description: string }> = {
  [Channel.CANAL_CONS]: {
    name: 'CANAL_CONS',
    description: 'Terminal local - Entrada de usuario en consola física del regulador'
  },
  [Channel.CANAL_ICOM]: {
    name: 'CANAL_ICOM',
    description: 'Intérprete de comandos - Procesa órdenes ASCII y binarias'
  },
  [Channel.CANAL_GCOM]: {
    name: 'CANAL_GCOM',
    description: 'Gestor de comunicaciones - Control de la red y mantenimiento'
  },
  [Channel.CANAL_DET]: {
    name: 'CANAL_DET',
    description: 'Gestión de detectores - Datos de tráfico e intensidades'
  },
  [Channel.CANAL_ALAR]: {
    name: 'CANAL_ALAR',
    description: 'Gestión de alarmas - Eventos y notificaciones del sistema'
  }
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function formatHex(value: number, bytes: number = 1): string {
  return '0x' + value.toString(16).toUpperCase().padStart(bytes * 2, '0');
}

function formatBinary(value: number): string {
  return value.toString(2).padStart(8, '0');
}

function bytesToHex(data: Uint8Array): string {
  return Array.from(data).map(b => b.toString(16).toUpperCase().padStart(2, '0')).join(' ');
}

export function parsePhysicalId(high: number, low: number): ParsedPhysicalId {
  const raw = (high << 8) | low;

  // Extract fields according to protocol spec:
  // Bits 14-15: Host (0-2)
  // Bits 8-13: Central (1-62, 63=unidentified)
  // Bits 6-7: Subregulator (1-2)
  // Bits 0-5: Regulator (1-62, 63=unidentified)

  const host = (raw >> 14) & 0x03;
  const central = (raw >> 8) & 0x3F;
  const subregulator = (raw >> 6) & 0x03;
  const regulator = raw & 0x3F;

  // Determine node level based on which fields are 0
  let level: NodeLevel;
  if (regulator === 0 && subregulator === 0 && central === 0) {
    level = NodeLevel.HOST;
  } else if (regulator === 0 && subregulator === 0) {
    level = NodeLevel.CENTRAL;
  } else if (subregulator === 0) {
    level = NodeLevel.REGULATOR;
  } else {
    level = NodeLevel.SUBREGULATOR;
  }

  const levelNames = ['HOST', 'CENTRAL', 'REGULADOR', 'SUBREGULADOR'];

  return {
    raw,
    host,
    central,
    regulator,
    subregulator,
    level,
    formatted: `H${host}-C${central}-R${regulator}-S${subregulator}`,
    explanation: `Nodo nivel ${levelNames[level]}: Host ${host}, Central ${central}, Regulador ${regulator}, Subregulador ${subregulator}`
  };
}

// =============================================================================
// MAIN PARSER
// =============================================================================

export function parseFrame(raw: Uint8Array): ParsedFrame {
  const rawHex = bytesToHex(raw);

  // Check for single-byte test frames
  if (raw.length === 1) {
    return parseTestFrame(raw, rawHex);
  }

  // Check for flow control frames (2 bytes: ESC + control)
  if (raw.length === 2 && raw[0] === ControlCodes.ESC) {
    return parseFlowControlFrame(raw, rawHex);
  }

  // Data frames start with STX
  if (raw[0] === ControlCodes.STX) {
    return parseDataFrame(raw, rawHex);
  }

  // Unknown frame type
  return {
    frameType: FrameType.ERROR,
    frameTypeName: 'DESCONOCIDO',
    frameTypeDescription: 'Trama no reconocida - no coincide con ningún formato del Protocolo B',
    raw,
    rawHex,
    summary: 'Trama de formato desconocido',
    educationalNotes: [
      'Las tramas válidas del Protocolo B son:',
      '• Test de comunicaciones: 1 byte (TOK=0x01, TML=0x06, TRT=0x07)',
      '• Control de flujo: 2 bytes (ESC + ACK0/ACK1/NAK/ENQ)',
      '• Datos: STX + datos + CRC + EOF'
    ]
  };
}

function parseTestFrame(raw: Uint8Array, rawHex: string): ParsedFrame {
  const code = raw[0];

  const testInfo: Record<number, { name: string; description: string }> = {
    [ControlCodes.TOK]: {
      name: 'TOK (Test OK)',
      description: 'Indica que la comunicación anterior fue correcta. El nodo activo (nivel superior) envía TOK cada 4 segundos si recibió respuesta al test anterior.'
    },
    [ControlCodes.TML]: {
      name: 'TML (Test Mal)',
      description: 'Indica error en comunicación anterior. Se envía cuando no hubo respuesta o fue incorrecta. Tras 3 TML consecutivos, el enlace pasa a estado "mal".'
    },
    [ControlCodes.TRT]: {
      name: 'TRT (Test Respuesta)',
      description: 'Respuesta del nodo pasivo (nivel inferior) a TOK o TML. Debe enviarse inmediatamente al recibir cualquier trama de test.'
    }
  };

  const info = testInfo[code] || { name: 'TEST DESCONOCIDO', description: 'Código de test no reconocido' };

  return {
    frameType: FrameType.TEST_COMMUNICATION,
    frameTypeName: 'TEST DE COMUNICACIONES',
    frameTypeDescription: 'Trama de un solo byte para verificar el estado del enlace entre dos nodos adyacentes.',
    raw,
    rawHex,
    testCode: code,
    testName: info.name,
    testDescription: info.description,
    summary: `Test de comunicaciones: ${info.name}`,
    educationalNotes: [
      '📡 El test de comunicaciones mantiene la red sincronizada:',
      '',
      '• El nodo ACTIVO (nivel superior) envía TOK/TML cada 4 segundos',
      '• El nodo PASIVO (nivel inferior) responde con TRT',
      '• El test PASIVO evalúa cada 5 segundos si recibió test',
      '',
      '📊 Estados del enlace:',
      '• DESCONOCIDO → 3 tests OK → BIEN',
      '• BIEN → 3 tests MAL → ERROR',
      '• Los estados intermedios cuentan errores/aciertos'
    ]
  };
}

function parseFlowControlFrame(raw: Uint8Array, rawHex: string): ParsedFrame {
  const control = raw[1];

  const flowInfo: Record<number, { name: string; description: string }> = {
    [ControlCodes.ACK0]: {
      name: 'ACK0 (Confirmación Par)',
      description: 'Confirma recepción correcta de trama sin encaminamiento o trama con encaminamiento de número par.'
    },
    [ControlCodes.ACK1]: {
      name: 'ACK1 (Confirmación Impar)',
      description: 'Confirma recepción correcta de trama con encaminamiento de número impar. Se alterna con ACK0 para detectar duplicados.'
    },
    [ControlCodes.NAK]: {
      name: 'NAK (Rechazo)',
      description: 'Rechaza la trama recibida por error (CRC inválido, formato incorrecto). El emisor debe reenviar hasta 3 veces.'
    },
    0x05: {
      name: 'ENQ (Comprobación)',
      description: 'Solicita reenvío de última confirmación. Se usa cuando no se recibe ACK/NAK en 1 segundo.'
    }
  };

  const info = flowInfo[control] || { name: 'CONTROL DESCONOCIDO', description: 'Código de control de flujo no reconocido' };

  return {
    frameType: FrameType.FLOW_CONTROL,
    frameTypeName: 'CONTROL DE FLUJO',
    frameTypeDescription: 'Trama de 2 bytes (ESC + control) para gestionar la entrega fiable de datos en enlaces serie.',
    raw,
    rawHex,
    flowControlType: info.name,
    flowControlDescription: info.description,
    summary: `Control de flujo: ${info.name}`,
    educationalNotes: [
      '🔄 Control de flujo (solo en enlaces serie, no en TCP):',
      '',
      '• Toda trama de datos debe confirmarse en <1 segundo',
      '• ACK0/ACK1 se alternan para mensajes largos (partidos)',
      '• NAK solicita reenvío (hasta 3 intentos)',
      '• ENQ se usa si no hay respuesta en 1 segundo',
      '',
      '⚠️ En enlaces Ethernet/TCP no se usa control de flujo',
      '   porque TCP ya garantiza la entrega fiable.'
    ]
  };
}

function parseDataFrame(raw: Uint8Array, rawHex: string): ParsedFrame {
  const result: ParsedFrame = {
    frameType: FrameType.INFORMATION,
    frameTypeName: 'TRAMA DE DATOS',
    frameTypeDescription: 'Trama con estructura STX + datos codificados + CRC + EOF',
    raw,
    rawHex,
    summary: '',
    educationalNotes: []
  };

  // Parse STX
  result.stx = {
    offset: 0,
    value: raw[0],
    hex: formatHex(raw[0]),
    binary: formatBinary(raw[0]),
    name: 'STX',
    description: 'Start of Text - Marca el inicio de una trama de datos'
  };

  // Find EOF
  const eofIndex = raw.length - 1;
  if (raw[eofIndex] !== ControlCodes.EOF) {
    result.summary = 'Error: No se encontró EOF al final de la trama';
    result.educationalNotes = ['Las tramas de datos deben terminar con EOF (0x1A)'];
    return result;
  }

  result.eof = {
    offset: eofIndex,
    value: raw[eofIndex],
    hex: formatHex(raw[eofIndex]),
    binary: formatBinary(raw[eofIndex]),
    name: 'EOF',
    description: 'End of File - Marca el final de la trama de datos'
  };

  // Extract and decode content between STX and EOF
  const encodedContent = raw.slice(1, eofIndex);
  result.encodedData = encodedContent;

  const decoded = decodeData(encodedContent);
  result.decodedData = decoded;

  if (decoded.length < 3) {
    result.summary = 'Error: Trama demasiado corta (mínimo 1 byte datos + 2 bytes CRC)';
    return result;
  }

  // Extract CRC (last 2 bytes of decoded data)
  const crcHigh = decoded[decoded.length - 2];
  const crcLow = decoded[decoded.length - 1];
  const receivedCRC = combineCRC(crcHigh, crcLow);

  // Data without CRC
  const data = decoded.slice(0, -2);

  // Verify CRC
  const crcValid = verifyCRC(decoded);

  result.crc = {
    received: receivedCRC,
    calculated: receivedCRC, // For display purposes
    valid: crcValid,
    bytes: [
      {
        offset: decoded.length - 2,
        value: crcHigh,
        hex: formatHex(crcHigh),
        binary: formatBinary(crcHigh),
        name: 'CRC (byte alto)',
        description: 'Byte más significativo del CRC-16'
      },
      {
        offset: decoded.length - 1,
        value: crcLow,
        hex: formatHex(crcLow),
        binary: formatBinary(crcLow),
        name: 'CRC (byte bajo)',
        description: 'Byte menos significativo del CRC-16'
      }
    ]
  };

  // Check if it's a routed frame (bit 6 of first data byte = 0)
  const firstByte = data[0];
  const isRouted = (firstByte & 0x40) === 0;

  if (isRouted && data.length >= 7) {
    result.frameType = FrameType.INFORMATION;
    parseRoutedFrame(result, data);
  } else if (!isRouted) {
    // Non-routed frame (identification or test response)
    parseNonRoutedFrame(result, data);
  }

  // Add educational notes based on CRC
  result.educationalNotes.push(
    '🔐 CRC-16 (Cyclic Redundancy Check):',
    '',
    `• Polinomio: x¹⁶ + x¹⁵ + x² + 1 (0x8005)`,
    '• Se inicializa a 0',
    '• Se calcula ANTES de codificar bytes con DLE',
    `• CRC recibido: ${formatHex(receivedCRC, 2)}`,
    crcValid ? '• ✅ CRC válido - datos íntegros' : '• ❌ CRC inválido - posible corrupción'
  );

  return result;
}

function parseRoutedFrame(result: ParsedFrame, data: Uint8Array): void {
  // Parse 7-byte header
  const header: ParsedHeader = {
    bytes: [],
    destinationChannel: data[0] & 0x1F,
    sourceChannel: data[1] & 0x1F,
    isResponse: (data[0] & 0x80) !== 0,
    isRouted: true,
    isMaintenance: (data[0] & 0x20) !== 0,
    expectsResponse: (data[1] & 0x20) !== 0,
    isContinuation: (data[1] & 0x80) !== 0,
    hasError: (data[1] & 0x40) !== 0,
    isCommunicationError: (data[1] & 0x20) !== 0 && (data[0] & 0x80) !== 0,
    destination: parsePhysicalId(data[2], data[3]),
    source: parsePhysicalId(data[4], data[5]),
    counter: data[6]
  };

  // Parse header byte 0 (destination channel)
  const destChannelInfo = CHANNEL_INFO[header.destinationChannel] || { name: `CANAL_${header.destinationChannel}`, description: 'Canal no estándar' };
  header.bytes.push({
    offset: 0,
    value: data[0],
    hex: formatHex(data[0]),
    binary: formatBinary(data[0]),
    name: 'Canal Destino',
    description: `Canal ${header.destinationChannel} (${destChannelInfo.name})`,
    bits: [
      { position: 7, value: header.isResponse, name: 'Respuesta', description: header.isResponse ? 'Es una respuesta a una orden' : 'Es un envío (orden o espontáneo)' },
      { position: 6, value: false, name: 'Encaminamiento', description: '0 = Trama con encaminamiento (pasa por nodos intermedios)' },
      { position: 5, value: header.isMaintenance, name: 'Tipo', description: header.isMaintenance ? 'Trama de mantenimiento de red' : 'Trama de información' },
      { position: 4, value: (data[0] & 0x10) !== 0, name: 'Canal[4]', description: `Bit 4 del canal destino` },
      { position: 3, value: (data[0] & 0x08) !== 0, name: 'Canal[3]', description: `Bit 3 del canal destino` },
      { position: 2, value: (data[0] & 0x04) !== 0, name: 'Canal[2]', description: `Bit 2 del canal destino` },
      { position: 1, value: (data[0] & 0x02) !== 0, name: 'Canal[1]', description: `Bit 1 del canal destino` },
      { position: 0, value: (data[0] & 0x01) !== 0, name: 'Canal[0]', description: `Bit 0 del canal destino` }
    ]
  });

  // Parse header byte 1 (source channel)
  const srcChannelInfo = CHANNEL_INFO[header.sourceChannel] || { name: `CANAL_${header.sourceChannel}`, description: 'Canal no estándar' };
  header.bytes.push({
    offset: 1,
    value: data[1],
    hex: formatHex(data[1]),
    binary: formatBinary(data[1]),
    name: 'Canal Origen',
    description: `Canal ${header.sourceChannel} (${srcChannelInfo.name})`,
    bits: [
      { position: 7, value: header.isContinuation, name: 'Continuación', description: header.isContinuation ? 'Mensaje partido - continúa en otra trama' : 'Mensaje completo o última parte' },
      { position: 6, value: header.hasError, name: 'Error', description: header.hasError ? 'Respuesta con error' : 'Sin error' },
      { position: 5, value: header.expectsResponse, name: 'Esp. Respuesta', description: header.isResponse ? (header.isCommunicationError ? 'Error de comunicación' : 'Respuesta normal') : (header.expectsResponse ? 'El origen espera respuesta' : 'No espera respuesta') },
      { position: 4, value: (data[1] & 0x10) !== 0, name: 'Canal[4]', description: `Bit 4 del canal origen` },
      { position: 3, value: (data[1] & 0x08) !== 0, name: 'Canal[3]', description: `Bit 3 del canal origen` },
      { position: 2, value: (data[1] & 0x04) !== 0, name: 'Canal[2]', description: `Bit 2 del canal origen` },
      { position: 1, value: (data[1] & 0x02) !== 0, name: 'Canal[1]', description: `Bit 1 del canal origen` },
      { position: 0, value: (data[1] & 0x01) !== 0, name: 'Canal[0]', description: `Bit 0 del canal origen` }
    ]
  });

  // Parse destination physical ID (bytes 2-3)
  header.bytes.push({
    offset: 2,
    value: data[2],
    hex: formatHex(data[2]),
    binary: formatBinary(data[2]),
    name: 'ID Físico Destino (alto)',
    description: `Byte alto de la dirección física destino`
  });
  header.bytes.push({
    offset: 3,
    value: data[3],
    hex: formatHex(data[3]),
    binary: formatBinary(data[3]),
    name: 'ID Físico Destino (bajo)',
    description: `Dirección completa: ${header.destination.formatted}`
  });

  // Parse source physical ID (bytes 4-5)
  header.bytes.push({
    offset: 4,
    value: data[4],
    hex: formatHex(data[4]),
    binary: formatBinary(data[4]),
    name: 'ID Físico Origen (alto)',
    description: `Byte alto de la dirección física origen`
  });
  header.bytes.push({
    offset: 5,
    value: data[5],
    hex: formatHex(data[5]),
    binary: formatBinary(data[5]),
    name: 'ID Físico Origen (bajo)',
    description: `Dirección completa: ${header.source.formatted}`
  });

  // Parse counter (byte 6)
  header.bytes.push({
    offset: 6,
    value: data[6],
    hex: formatHex(data[6]),
    binary: formatBinary(data[6]),
    name: 'Contador',
    description: `Número de secuencia: ${data[6]}. Permite correlacionar respuestas con órdenes.`
  });

  result.header = header;

  // Parse payload (bytes after header)
  const payload = data.slice(7);
  if (payload.length > 0) {
    parseCommand(result, payload);
  }

  // Build summary
  const direction = header.isResponse ? '← RESPUESTA' : '→ ORDEN';
  const frameKind = header.isMaintenance ? 'MANTENIMIENTO' : 'INFORMACIÓN';
  result.summary = `${direction} ${frameKind}: ${header.source.formatted} → ${header.destination.formatted}`;

  if (result.command) {
    result.summary += ` | ${result.command.name}`;
  }

  // Add educational notes for routing
  result.educationalNotes.unshift(
    '📍 Encaminamiento de tramas:',
    '',
    '• Las tramas viajan por la red jerárquica',
    '• Cada nodo examina la dirección destino',
    '• Si coincide con su ID → procesa la trama',
    '• Si es de nivel inferior → reenvía al subnodo',
    '• Si no encuentra ruta → envía al nodo superior',
    ''
  );
}

function parseNonRoutedFrame(result: ParsedFrame, data: Uint8Array): void {
  const firstByte = data[0];

  // Check for identification frames (bit 6 = 1)
  if ((firstByte & 0x40) !== 0) {
    result.frameType = FrameType.IDENTIFICATION;

    const level = firstByte & 0x03;
    const isRequest = (firstByte & 0x04) !== 0;
    const isLogical = (firstByte & 0x08) !== 0;
    const isFromSender = (firstByte & 0x80) !== 0;

    let frameSubtype = '';
    let description = '';

    if ((firstByte & 0xFC) === 0x44) {
      frameSubtype = 'QUIEN_SOY';
      description = 'El nodo inferior solicita ser identificado por su nodo superior';
    } else if ((firstByte & 0xFC) === 0x40) {
      frameSubtype = 'ERES_FISICO';
      description = 'El nodo superior asigna dirección física al nodo inferior';
    } else if ((firstByte & 0xFC) === 0xC8) {
      frameSubtype = 'SOY_LOGICO';
      description = 'El nodo inferior comunica su identificación lógica, tipo y versión';
    }

    result.frameTypeName = `IDENTIFICACIÓN: ${frameSubtype}`;
    result.frameTypeDescription = description;
    result.summary = `Identificación ${frameSubtype} - Nivel ${['HOST', 'CENTRAL', 'REGULADOR', 'SUBREGULADOR'][level]}`;

    result.educationalNotes = [
      '🔗 Proceso de identificación:',
      '',
      '1. Subnodo envía QUIEN_SOY al conectarse',
      '2. Nodo superior responde con ERES_FISICO (asigna dirección)',
      '3. Subnodo confirma con SOY_LOGICO (envía su ID lógica)',
      '',
      '• La ID física indica posición en la red (H-C-R-S)',
      '• La ID lógica identifica al equipo (1-32767)',
      '• El proceso se repite si cambia la ID lógica'
    ];
  }

  // Check for test response frames
  if ((firstByte & 0xF0) === 0x50) {
    result.frameType = FrameType.TEST_RESPONSE;
    result.frameTypeName = 'RESPUESTA A TEST';

    const errorCode = firstByte & 0x0F;
    const errorDescriptions: Record<number, string> = {
      0x00: 'RTST_CORRECTO - Test de respuesta realizado correctamente',
      0x01: 'RTST_NO_RESP - El nodo destino no respondió',
      0x02: 'RTST_OCUPADO - El nodo recibió otro test antes de responder',
      0x03: 'RTST_NPAO - Intérprete de comandos ocupado (3 intentos)',
      0x04: 'RTST_TO_ICOM - Timeout del intérprete de comandos',
      0x05: 'RTST_ER_ICOM - Error del intérprete de comandos',
      0x06: 'RTST_ER_DISTRI - Error al encaminar mensaje interno',
      0x07: 'RTST_TO_EQ - Timeout en test de equipos'
    };

    result.frameTypeDescription = errorDescriptions[errorCode] || 'Código de error desconocido';
    result.summary = `Respuesta test: ${errorCode === 0 ? '✅ OK' : '❌ Error ' + errorCode}`;

    result.educationalNotes = [
      '🔍 Test de respuesta:',
      '',
      '• Verifica que el nodo está funcionando',
      '• Se realiza cada n×5 segundos (n=1-16)',
      '• Consulta al intérprete de comandos interno',
      '',
      '📊 Códigos de error:',
      '• 0: OK',
      '• 1-6: Problemas internos del nodo',
      '• 7: Timeout en test de equipos',
      '',
      '⚠️ 2 errores RTST_TO_EQ consecutivos → nodo de baja'
    ];
  }
}

function parseCommand(result: ParsedFrame, payload: Uint8Array): void {
  if (payload.length === 0) return;

  // Try to identify command
  let cmd: CommandDefinition | undefined;
  let cmdOffset = 0;

  // First try two-byte command
  if (payload.length >= 2) {
    const twoByteCode = (payload[0] << 8) | payload[1];
    const twoByteDef = TWO_BYTE_COMMANDS[twoByteCode];
    if (twoByteDef) {
      cmd = { code: twoByteCode, ...twoByteDef };
      cmdOffset = 2;
    }
  }

  // Then try single-byte command
  if (!cmd) {
    const singleByteDef = SINGLE_BYTE_COMMANDS[payload[0]];
    if (singleByteDef) {
      cmd = { code: payload[0], ...singleByteDef };
      cmdOffset = 1;
    }
  }

  if (cmd) {
    result.command = {
      code: cmd.code,
      name: cmd.name,
      category: cmd.category,
      description: cmd.description,
      parameters: [],
      expectedResponse: cmd.responseDescription
    };

    // Parse parameters
    const paramData = payload.slice(cmdOffset);
    let paramOffset = 0;

    for (const paramDef of cmd.parameters) {
      if (paramOffset >= paramData.length) break;

      const length = paramDef.length === -1
        ? paramData.length - paramOffset
        : paramDef.length;

      const rawValue = paramData.slice(paramOffset, paramOffset + length);
      let decodedValue: string | number = bytesToHex(rawValue);

      if (paramDef.decoder) {
        try {
          decodedValue = paramDef.decoder(rawValue);
        } catch {}
      }

      result.command.parameters.push({
        name: paramDef.name,
        offset: cmdOffset + paramOffset,
        length,
        rawValue,
        decodedValue,
        description: paramDef.description,
        unit: paramDef.unit
      });

      paramOffset += length;
    }

    // Add command-specific educational notes
    result.educationalNotes.push(
      '',
      `📋 Orden: ${cmd.name}`,
      `   Categoría: ${cmd.category}`,
      '',
      cmd.description,
      '',
      `📨 Respuesta esperada: ${cmd.responseDescription}`
    );
  } else {
    // Unknown command
    result.payload = payload.map((byte, i) => ({
      offset: i,
      value: byte,
      hex: formatHex(byte),
      binary: formatBinary(byte),
      name: `Byte ${i}`,
      description: i === 0 ? 'Código de comando no reconocido' : 'Datos'
    }));

    result.educationalNotes.push(
      '',
      `❓ Comando no reconocido: ${formatHex(payload[0])}`,
      '',
      'Puede ser:',
      '• Una orden ASCII (texto)',
      '• Una orden propietaria',
      '• Datos de respuesta'
    );
  }
}
